import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Layers, Users, GraduationCap, BookOpen, AlertTriangle, CheckCircle, MapPin, ShieldAlert, Clock, Pause, CalendarCheck, RefreshCw, ArrowRight, XCircle } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { ZohoCustomerLink } from '../../components/ZohoLinks';

const PIE_COLORS = ['#10b981', '#6366f1', '#f59e0b', '#8b5cf6', '#ef4444', '#64748b', '#06b6d4'];

function KpiCard({ label, value, icon: Icon, color = 'text-slate-900', accent, onClick, sub }) {
  return (
    <Card className={`border p-4 transition-all ${onClick ? 'cursor-pointer hover:border-violet-300 hover:shadow-sm' : ''}`} onClick={onClick} data-testid={`kpi-${label.toLowerCase().replace(/[\s/]/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5 truncate">{label}</p>
          <p className={`text-xl font-bold ${color}`}>{value}</p>
          {sub && <p className="text-[9px] text-slate-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${accent || 'bg-slate-50'}`}>
          <Icon className={`w-4 h-4 ${accent ? 'text-white' : 'text-slate-400'}`} />
        </div>
      </div>
    </Card>
  );
}

function fmtFull(n) {
  return n != null ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-';
}

export default function TrainingDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/training/dashboard').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div></div>;
  if (!data) return <div className="text-center py-20 text-slate-400">Failed to load dashboard data</div>;

  return (
    <div className="space-y-6" data-testid="training-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Training Dashboard</h1>
          <p className="text-sm text-slate-400 mt-0.5">Overview of training operations</p>
        </div>
        {data.last_sync && (
          <div className="text-right">
            <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Last Sync</p>
            <p className="text-xs text-slate-500">{data.last_sync.type?.toUpperCase()} &middot; <Badge variant="outline" className={`text-[9px] ${data.last_sync.status === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{data.last_sync.status}</Badge></p>
            <p className="text-[10px] text-slate-400">{data.last_sync.completed_at ? new Date(data.last_sync.completed_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
          </div>
        )}
      </div>

      {/* Row 1: Batch KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        <KpiCard label="Total Batches" value={data.total_batches} icon={Layers} accent="bg-violet-500" onClick={() => navigate('/training/batches')} />
        <KpiCard label="Running" value={data.running} icon={Layers} color="text-emerald-600" accent="bg-emerald-500" onClick={() => navigate('/training/batches')} />
        <KpiCard label="Completed" value={data.completed} icon={CheckCircle} color="text-blue-600" onClick={() => navigate('/training/batches')} />
        <KpiCard label="On Hold" value={data.on_hold} icon={Pause} color="text-amber-600" onClick={() => navigate('/training/batches')} />
        <KpiCard label="Upcoming" value={data.upcoming} icon={CalendarCheck} color="text-violet-600" onClick={() => navigate('/training/batches')} />
        <KpiCard label="Delayed" value={data.delayed} icon={AlertTriangle} color={data.delayed > 0 ? 'text-red-600' : 'text-slate-900'} accent={data.delayed > 0 ? 'bg-red-500' : undefined} onClick={() => navigate('/training/batches')} />
        <KpiCard label="Cancelled" value={data.cancelled} icon={XCircle} color="text-slate-500" />
      </div>

      {/* Row 2: Student & Master KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard label="Total Students" value={data.total_students} icon={Users} accent="bg-indigo-500" onClick={() => navigate('/training/students')} />
        <KpiCard label="Active Trainers" value={data.total_trainers} icon={GraduationCap} onClick={() => navigate('/training/reports?tab=trainer')} />
        <KpiCard label="Total Courses" value={data.total_courses} icon={BookOpen} onClick={() => navigate('/training/reports?tab=operational&group_by=course')} />
        <KpiCard label="Total Centres" value={data.total_centres} icon={MapPin} onClick={() => navigate('/training/reports?tab=operational&group_by=centre')} />
      </div>

      {/* Row 3: Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Batch Status Distribution - Pie Chart */}
        <Card className="border p-5" data-testid="chart-status-dist">
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3">Batch Status Distribution</p>
          {data.status_distribution?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={data.status_distribution} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={2} label={({ status, count }) => `${status} (${count})`} labelLine={false} style={{ fontSize: '10px' }}>
                  {data.status_distribution.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [v, n]} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-12">No data</p>}
        </Card>

        {/* Monthly Batch Starts - Bar Chart */}
        <Card className="border p-5" data-testid="chart-monthly-starts">
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3">Monthly Batch Starts</p>
          {data.monthly_starts?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.monthly_starts} margin={{ top: 5, right: 5, bottom: 5, left: -15 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 9 }} tickFormatter={m => { const [y, mo] = m.split('-'); return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(mo)-1]} ${y.slice(2)}`; }} />
                <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                <Tooltip labelFormatter={m => { const [y, mo] = m.split('-'); return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(mo)-1]} ${y}`; }} />
                <Bar dataKey="count" fill="#7c3aed" radius={[3, 3, 0, 0]} name="Batches Started" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-12">No data</p>}
        </Card>

        {/* Centre-wise Distribution - Horizontal Bar Chart */}
        <Card className="border p-5" data-testid="chart-centre-dist">
          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3">Batches by Centre</p>
          {data.centre_distribution?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.centre_distribution} layout="vertical" margin={{ top: 5, right: 10, bottom: 5, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 9 }} allowDecimals={false} />
                <YAxis dataKey="centre" type="category" tick={{ fontSize: 9 }} width={80} />
                <Tooltip />
                <Bar dataKey="count" fill="#6366f1" radius={[0, 3, 3, 0]} name="Batches" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-12">No data</p>}
        </Card>
      </div>

      {/* Row 5: Quick Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recently Started Batches */}
        <Card className="border p-0" data-testid="recent-batches-table">
          <div className="px-4 py-3 border-b"><p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Recently Started Batches</p></div>
          {data.recent_batches?.length > 0 ? (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-[9px] uppercase">Batch #</TableHead>
                <TableHead className="text-[9px] uppercase">Name</TableHead>
                <TableHead className="text-[9px] uppercase">Start</TableHead>
                <TableHead className="text-[9px] uppercase">Students</TableHead>
                <TableHead className="text-[9px] uppercase">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.recent_batches.map(b => (
                  <TableRow key={b.id} className="cursor-pointer hover:bg-violet-50/30" onClick={() => navigate('/training/batches')}>
                    <TableCell className="text-xs font-medium text-violet-600">{b.batch_number ? `#${b.batch_number}` : '-'}</TableCell>
                    <TableCell className="text-xs">{(b.name || b.course_name || '').substring(0, 25)}</TableCell>
                    <TableCell className="text-[10px] text-slate-500">{b.start_date}</TableCell>
                    <TableCell className="text-xs text-center">{b.student_count || 0}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[9px]">{b.batch_status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : <p className="text-xs text-slate-400 text-center py-6">No recent batches</p>}
        </Card>

        {/* Upcoming Batches */}
        <Card className="border p-0" data-testid="upcoming-batches-table">
          <div className="px-4 py-3 border-b"><p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Upcoming Batches</p></div>
          {data.upcoming_batches?.length > 0 ? (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-[9px] uppercase">Batch #</TableHead>
                <TableHead className="text-[9px] uppercase">Name</TableHead>
                <TableHead className="text-[9px] uppercase">Start</TableHead>
                <TableHead className="text-[9px] uppercase">Trainer</TableHead>
                <TableHead className="text-[9px] uppercase">Students</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.upcoming_batches.map(b => (
                  <TableRow key={b.id} className="cursor-pointer hover:bg-violet-50/30" onClick={() => navigate('/training/batches')}>
                    <TableCell className="text-xs font-medium text-violet-600">{b.batch_number ? `#${b.batch_number}` : '-'}</TableCell>
                    <TableCell className="text-xs">{(b.name || b.course_name || '').substring(0, 25)}</TableCell>
                    <TableCell className="text-[10px] text-slate-500">{b.start_date}</TableCell>
                    <TableCell className="text-xs text-slate-500">{(b.trainer_name || '-').substring(0, 18)}</TableCell>
                    <TableCell className="text-xs text-center">{b.student_count || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : <p className="text-xs text-slate-400 text-center py-6">No upcoming batches</p>}
        </Card>

        {/* Most Delayed Batches */}
        <Card className="border p-0" data-testid="delayed-batches-table">
          <div className="px-4 py-3 border-b"><p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Most Delayed Batches</p></div>
          {data.most_delayed?.length > 0 ? (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-[9px] uppercase">Batch #</TableHead>
                <TableHead className="text-[9px] uppercase">Name</TableHead>
                <TableHead className="text-[9px] uppercase">End Date</TableHead>
                <TableHead className="text-[9px] uppercase">Delay</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.most_delayed.map(b => (
                  <TableRow key={b.id} className="cursor-pointer hover:bg-red-50/30" onClick={() => navigate('/training/batches')}>
                    <TableCell className="text-xs font-medium text-violet-600">{b.batch_number ? `#${b.batch_number}` : '-'}</TableCell>
                    <TableCell className="text-xs">{(b.name || b.course_name || '').substring(0, 25)}</TableCell>
                    <TableCell className="text-[10px] text-slate-500">{b.end_date}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[9px] bg-red-50 text-red-600 border-red-200"><AlertTriangle className="w-3 h-3 mr-0.5" />{b.delay_days}d late</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : <p className="text-xs text-slate-400 text-center py-6">No delayed batches</p>}
        </Card>

        {/* Top Overdue Students */}
        <Card className="border p-0" data-testid="overdue-students-table">
          <div className="px-4 py-3 border-b"><p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Top Overdue Students</p></div>
          {data.top_overdue_students?.length > 0 ? (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-[9px] uppercase">Enrollment</TableHead>
                <TableHead className="text-[9px] uppercase">Name</TableHead>
                <TableHead className="text-[9px] uppercase">Course</TableHead>
                <TableHead className="text-[9px] uppercase text-right">Due Amount</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.top_overdue_students.map(s => (
                  <TableRow key={s.id} className="cursor-pointer hover:bg-amber-50/30" onClick={() => navigate('/training/students')}>
                    <TableCell className="text-xs"><ZohoCustomerLink enrollmentNo={s.enrollment_no} zohoCustomerId={s.zoho_customer_id} /></TableCell>
                    <TableCell className="text-xs">{(s.name || '').substring(0, 22)}</TableCell>
                    <TableCell className="text-[10px] text-slate-500">{(s.course || '-').substring(0, 18)}</TableCell>
                    <TableCell className="text-xs text-right text-red-600 font-medium">{fmtFull(s.due_amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : <p className="text-xs text-slate-400 text-center py-6">No overdue students</p>}
        </Card>
      </div>
    </div>
  );
}
